import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Drawer,
  Fab,
  IconButton,
  Typography,
  styled,
  Button
} from '@mui/material';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  // Email as EmailIcon,
  Phone as PhoneIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import CustomTextField from 'src/components/CustomInputs/CustomTextField';
import { sendWhatsAppMessageAPI } from 'src/api/dataServices';
import { useLocation } from 'react-router-dom';

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  zIndex: 1300
}));

const MessageBox = styled(Box)(({ isUser }) => ({
  padding: '12px',
  marginBottom: '8px',
  maxWidth: '80%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  backgroundColor: isUser ? '#88F231' : '#f5f5f5',
  color: isUser ? '#000' : '#333',
  borderRadius: 12
}));

const MessagesContainer = styled(Box)({
  flexGrow: 1,
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
});

const ContactButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderTop: '1px solid #eee',
  backgroundColor: '#fafafa'
}));

const DrawerPaper = {
  width: 350,
  height: 450,
  borderRadius: 2,
  position: 'fixed',
  bottom: 94,
  top: 'auto',
  right: 32,
  boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

const serviceOptions = [
  'Residential Loan',
  'C&I Financing Loan',
  'EPC Supply Chain Loan',
  'Housing Society Loan',
  'Install Solar'
];

const entityOptions = [
  'Individual',
  'Housing Society',
  'Commercial and Industrial',
  'Business'
];

// Step Configurations
const STEP_CONFIG = {
  0: {
    message: 'Hi! 👋 Enter your WhatsApp number to start.',
    inputType: 'text'
  },
  1: {
    message: 'Please enter your name.',
    inputType: 'text'
  },
  2: {
    message: 'Which service are you interested in?',
    inputType: 'cta',
    options: serviceOptions
  },
  3: {
    message: 'Select Your entity type:',
    inputType: 'cta',
    options: entityOptions
  },
  4: {
    inputType: 'text'
  },
  31: {
    message: 'What is your average monthly electricity bill?',
    inputType: 'text'
  },
  32: {
    message: 'System size required (kW)?',
    inputType: 'text'
  }
};

const getServiceQuestion = (service) => {
  if (service === 'EPC Supply Chain Loan') {
    return 'Please provide details, how can we help you with EPC Supply Chain finance?';
  } else {
    return 'Can you please describe your requirements in short?';
  }
};

const RaiseQuery = ({
  supportEmail = 'support@solfin.co.in',
  supportPhone = '+919599446877'
}) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [formData, setFormData] = useState({});
  const [step, setStep] = useState(0);
  const [inputType, setInputType] = useState('text');
  const [inputValue, setInputValue] = useState('');
  const [ctaOptions, setCtaOptions] = useState([]);
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const resetChat = useCallback(() => {
    setMessages([{ text: STEP_CONFIG[0].message, isUser: false }]);
    setFormData({});
    setStep(0);
    setInputType(STEP_CONFIG[0].inputType);
    setInputValue('');
    setCtaOptions([]);
  }, []);

  const pushMessage = useCallback((text, isUser = false) => {
    setMessages((prev) => [...prev, { text, isUser }]);
  }, []);

  const finalizeChat = useCallback(
    async (customFormData) => {
      const {
        name,
        selectedService,
        mobile,
        entityType,
        averageBill,
        systemSize
      } = customFormData || formData;

      const isInstallSolar = selectedService === 'Install Solar';

      const systemSizeText =
        systemSize !== undefined && systemSize !== ''
          ? `• System Size: ${systemSize}\n`
          : '';

      const billText =
        averageBill !== undefined && averageBill !== ''
          ? `• Avg. Monthly Bill: ₹${averageBill}\n`
          : '';

      const entityText = entityType ? `• Entity: ${entityType}\n` : '';

      const summary = isInstallSolar
        ? `✅ Thanks, ${name}!\n• Service: ${selectedService}\n${entityText}${billText}${systemSizeText}Your request has been logged. Our team will contact you via WhatsApp or call shortly!`
        : `✅ Thanks, ${name}!\n• Service: ${selectedService}\nWe’ve logged your request. Our experts will be in touch soon!`;

      try {
        const templateParams = isInstallSolar
          ? `: • Service: ${selectedService} ${entityText}${billText}${systemSizeText}`.trim()
          : `: • Service: ${selectedService}`;

        const response = await sendWhatsAppMessageAPI({
          whatsAppNumber: mobile,
          templateParams
        });

        if (!response.success) {
          setTimeout(() => {
            pushMessage(
              '❌ Unable to connect to WhatsApp service. Please try again later.',
              false
            );
          }, 1000);
          return;
        }
      } catch (error) {
        setTimeout(() => {
          pushMessage(
            '⚠️ Failed to send WhatsApp message. Please try again later.'
          );
        }, 1000);
        return;
      }

      pushMessage(summary);

      setTimeout(() => {
        pushMessage('🔄 Start a new conversation?', false);
        setCtaOptions(['Start a new chat']);
        setInputType('cta');
        setStep(5);
      }, 1500);
    },
    [formData, pushMessage]
  );

  const transitionToStep = useCallback(
    async (newStep, message, inputType, options = []) => {
      setStep(newStep);
      setInputType(inputType);
      setCtaOptions(options);
      if (message) {
        setTimeout(() => pushMessage(message), 500);
      }
    },
    [pushMessage]
  );

  const handleUserInput = useCallback(
    async (input) => {
      const trimmed = input.trim();
      if (!trimmed) return;

      pushMessage(trimmed, true);
      setInputValue('');

      switch (step) {
        case 0: {
          const number = trimmed.replace(/\D/g, '');
          if (!/^\d{10}$/.test(number)) {
            pushMessage('⚠️ Please enter a valid 10-digit WhatsApp number.');
            return;
          }

          setFormData((prev) => ({ ...prev, mobile: number }));
          try {
            const result = await sendWhatsAppMessageAPI({
              whatsAppNumber: number
            });
            if (!result.success) {
              setTimeout(() => {
                pushMessage(
                  '❌ Unable to connect to WhatsApp service. Please try again later.',
                  false
                );
              }, 1000);
              return;
            }
          } catch (error) {
            // Handle error from API
            setTimeout(() => {
              pushMessage(
                '⚠️ Failed to send WhatsApp message. Please try again later.'
              );
            }, 1000);
            return;
          }
          await transitionToStep(
            1,
            STEP_CONFIG[1].message,
            STEP_CONFIG[1].inputType
          );
          break;
        }

        case 1:
          setFormData((prev) => ({ ...prev, name: trimmed }));
          await transitionToStep(
            2,
            STEP_CONFIG[2].message,
            STEP_CONFIG[2].inputType,
            STEP_CONFIG[2].options
          );
          break;

        case 4:
          setFormData((prev) => ({ ...prev, notes: trimmed }));
          await finalizeChat();
          break;

        case 31:
          setFormData((prev) => ({ ...prev, averageBill: trimmed }));
          await transitionToStep(
            32,
            STEP_CONFIG[32].message,
            STEP_CONFIG[32].inputType
          );
          break;

        case 32: {
          const newData = { ...formData, systemSize: trimmed };
          setFormData((prev) => ({ ...prev, systemSize: trimmed }));
          await finalizeChat(newData);
          break;
        }
        default:
          break;
      }
    },
    [finalizeChat, pushMessage, step, formData, transitionToStep]
  );

  const handleCTASelection = useCallback(
    async (value) => {
      pushMessage(value, true);

      switch (step) {
        case 2:
          setFormData((prev) => ({ ...prev, selectedService: value }));

          if (value === 'Install Solar') {
            await transitionToStep(
              3,
              STEP_CONFIG[3].message,
              STEP_CONFIG[3].inputType,
              STEP_CONFIG[3].options
            );
          } else {
            const question = getServiceQuestion(value);
            await transitionToStep(4, question, STEP_CONFIG[4].inputType);
          }
          break;

        case 3:
          setFormData((prev) => ({ ...prev, entityType: value }));
          await transitionToStep(
            31,
            STEP_CONFIG[31].message ||
              'What is your average monthly electricity bill?',
            STEP_CONFIG[31].inputType
          );
          break;

        case 5:
          resetChat();
          break;

        default:
          break;
      }
    },
    [pushMessage, resetChat, step, transitionToStep]
  );

  const handleContactMethod = useCallback(
    (method) => {
      const number = supportPhone.replace(/\D/g, '');
      switch (method) {
        case 'whatsapp':
          window.open(`https://wa.me/ ${number}`, '_blank');
          break;
        case 'email':
          window.location.href = `mailto:${supportEmail}`;
          break;
        case 'phone':
          window.location.href = `tel:${supportPhone}`;
          break;
        default:
          break;
      }
    },
    [supportEmail, supportPhone]
  );

  // Reset chat when drawer opens
  useEffect(() => {
    if (open) resetChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Scroll to latest message
  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    // Close drawer whenever the route changes
    setOpen(false);
  }, [location.pathname]);
  return (
    <>
      <StyledFab color="primary" onClick={() => setOpen(!open)}>
        {!open ? <ChatIcon /> : <CloseIcon />}
      </StyledFab>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: DrawerPaper }}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Typography variant="subtitle1" sx={{ color: '#000' }}>
            Solfin Support
          </Typography>
        </Box>

        <MessagesContainer>
          {messages.map((msg, i) => (
            <MessageBox key={i} isUser={msg.isUser}>
              <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                {msg.text}
              </Typography>
            </MessageBox>
          ))}
          <Box ref={messagesEndRef} />
        </MessagesContainer>

        {inputType === 'cta' && ctaOptions.length > 0 && (
          <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {ctaOptions.map((option) => (
              <Button
                key={option}
                variant="outlined"
                color="text.secondary"
                onClick={() => {
                  if (step === 5) resetChat();
                  else handleCTASelection(option);
                }}
              >
                {option}
              </Button>
            ))}
          </Box>
        )}

        {inputType === 'text' && (
          <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
            <CustomTextField
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your response..."
              onKeyPress={(e) =>
                e.key === 'Enter' && handleUserInput(inputValue)
              }
            />
            <IconButton
              onClick={() => handleUserInput(inputValue)}
              sx={{ color: '#88F231' }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        )}

        <ContactButtons>
          <Typography variant="body1" color="text.secondary">
            Contact Us:
          </Typography>
          <IconButton
            onClick={() => handleContactMethod('whatsapp')}
            sx={{ color: '#25D366' }}
          >
            <WhatsAppIcon />
          </IconButton>
          {/* <IconButton
            onClick={() => handleContactMethod('email')}
            sx={{ color: '#88F231' }}
          >
            <EmailIcon />
          </IconButton> */}
          <IconButton
            onClick={() => handleContactMethod('phone')}
            sx={{ color: '#25D366' }}
          >
            <PhoneIcon />
          </IconButton>
        </ContactButtons>
      </Drawer>
    </>
  );
};

export default RaiseQuery;
